import { MutableRefObject, useEffect, useRef } from "react";
import {
  NotFoundOption,
  StyledTypo,
  StyledSearch,
} from "./AutocompleteMenu.style";
import Highlighter from "react-highlight-words";
import { createAutocompleteDisplayValue } from "@src/utils/utils";

type AutocompleteMenuProps = {
  searchInputRef: MutableRefObject<HTMLLdInputElement | null>;
  suggestions?: any[];
  selectSuggestion?: (suggestion: any) => void;
  isLoading?: boolean;
  isOpen?: boolean;
  focusedIndex?: number;
  inputValue?: string;
  min?: number;

};

const AutocompleteMenu = ({
  searchInputRef,
  suggestions,
  selectSuggestion,
  isLoading,
  isOpen,
  focusedIndex = -1,
  inputValue,
  min = 1,
}: AutocompleteMenuProps) => {
  const width = searchInputRef.current?.getBoundingClientRect().width || 0;
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (focusedIndex >= 0 && itemRefs.current[focusedIndex]) {
      itemRefs.current[focusedIndex]?.scrollIntoView({
        block: "nearest",
      });
    }
  }, [focusedIndex]);

  if (!isOpen) {
    return null;
  }

  if (!inputValue || inputValue.length < min) {
    return null;
  }

  if (isLoading) {
    return <StyledSearch>Searching...</StyledSearch>;
  }

  if (!suggestions) {
    return null;
  }


  return (
    <div
      data-testid="autocomplete-menu"
      style={{
        width: width * 0.5,
        maxHeight: "140px",
        minHeight: "40px",
        overflowY: "auto",
        overflowX: "hidden",
        position: "absolute",
        top: "100%",
        left: 0,
        zIndex: 1000,
        background: "#fff",
        border: "1px solid #d9d9d9",
        borderRadius: "4px",
        marginTop: "6px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      }}
    >

      {suggestions.length > 0 ? (
        suggestions.map((suggestion: any, i: number) => (
          <div
            key={i}
            ref={(el) => (itemRefs.current[i] = el)}
            onClick={() => selectSuggestion?.(suggestion)}
            style={{
              padding: "8px 12px",
              cursor: "pointer",
              background: focusedIndex === i ? "#f5f5f5" : "transparent",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <StyledTypo variant="body-m">
              <Highlighter
                searchWords={[searchInputRef.current?.value || ""]}
                autoEscape={true}
                textToHighlight={createAutocompleteDisplayValue(suggestion)}
              />
            </StyledTypo>
          </div>
        ))
      ) : (
        <NotFoundOption>
          <StyledTypo variant="body-m">No results found...</StyledTypo>
        </NotFoundOption>
      )}
    </div>
  );
};

export { AutocompleteMenu };
